import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Spinner,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { dates } from '@homevest/utils'
import { TrashIcon } from 'components/TailwindUIToolkit/StyledComponents'
import { Note_Priorities_Enum, useUpupInsertNoteMutation } from 'graphql/generated'
import axios from 'lib/axios'
import { BooksLockedNotice } from 'lib/buildiumBooksLock'
import { LedgerItem } from 'lib/ledger'
import React, { FC, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { StoreState } from 'store'
import BuildiumReversalDateNotice from './BuildiumReversalDateNotice'
import useBuildiumReversalDateChoice from './useBuildiumReversalDateChoice'

interface DeleteButtonProps {
  ledgerItem: Pick<LedgerItem, 'id' | 'type' | 'amount' | 'date' | 'liabilityLabel'>
  rentalId: string
  blockedDeleteMessage?: string | null
  preferTodayReversal?: boolean
  onSuccess?: () => void | Promise<void>
}

const PAYMENT_PLAN_DELETE_ERROR_MESSAGE =
  'This liability is part of a payment plan and cannot be deleted. Remove the payment plan first.'

const getDeleteErrorMessage = (error: unknown, type: DeleteButtonProps['ledgerItem']['type']) => {
  if (error && typeof error === 'object') {
    const graphQLErrors = 'graphQLErrors' in error ? error.graphQLErrors : undefined

    if (Array.isArray(graphQLErrors)) {
      const firstGraphQLError = graphQLErrors.find(
        (graphQLError) =>
          graphQLError &&
          typeof graphQLError === 'object' &&
          'message' in graphQLError &&
          typeof graphQLError.message === 'string'
      )

      if (firstGraphQLError?.message.includes('payment_plan_liabilities')) {
        return PAYMENT_PLAN_DELETE_ERROR_MESSAGE
      }

      if (firstGraphQLError?.message) {
        return firstGraphQLError.message
      }
    }

    const responseMessage =
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string'
        ? error.response.data.message
        : undefined

    if (responseMessage) {
      if (responseMessage.includes('payment_plan_liabilities')) {
        return PAYMENT_PLAN_DELETE_ERROR_MESSAGE
      }

      return responseMessage
    }

    const directMessage = 'message' in error ? error.message : undefined

    if (typeof directMessage === 'string' && directMessage) {
      if (directMessage.includes('payment_plan_liabilities')) {
        return PAYMENT_PLAN_DELETE_ERROR_MESSAGE
      }

      return directMessage.replaceAll('[GraphQL] ', '')
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return `Unable to delete this ${type.toLowerCase()}`
}

export const DeleteButton: FC<React.PropsWithChildren<DeleteButtonProps>> = ({
  ledgerItem,
  rentalId,
  blockedDeleteMessage = null,
  preferTodayReversal = false,
  onSuccess
}) => {
  const { type, id, amount, date, liabilityLabel } = ledgerItem
  const [isDeleting, setIsDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [_n, insertNote] = useUpupInsertNoteMutation()
  const [deleteReason, setDeleteReason] = useState<string>()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef(null)
  const admin = useSelector((state: StoreState) => state.admin)
  const usesBuildiumReversal = type === 'Charge' || type === 'Credit'
  const {
    booksLock,
    canReverseOriginal,
    canReverseToday,
    hasBuildiumReversalOption,
    isBooksLockError,
    isBooksLockLoading,
    originalReversalDate,
    resetReversalDateChoice,
    selectedReversalDate,
    setSelectedReversalDate,
    today
  } = useBuildiumReversalDateChoice({
    entryDate: date,
    isEnabled: usesBuildiumReversal,
    isOpen,
    preferToday: preferTodayReversal,
    rentalId
  })
  const ledgerItemLabel = type === 'Charge' ? 'liability' : 'credit'

  const handleDeletion = useCallback(async () => {
    if (usesBuildiumReversal && !selectedReversalDate) {
      return
    }

    setIsDeleting(true)
    setErrorMessage(null)

    try {
      const deleteConfig = usesBuildiumReversal
        ? { data: { buildium_reversal_date: selectedReversalDate } }
        : undefined

      if (type === 'Charge') {
        await axios.delete(`/admin/rental_liabilities/${id}`, deleteConfig)
      } else if (type === 'Credit') {
        await axios.delete(`/admin/rental_credits/${id}`, deleteConfig)
      }

      let noteText = `Deleted ${dates.formatISODate(
        date
      )} ${liabilityLabel} ${type} (${id}) for $${amount}`
      if (deleteReason) {
        noteText += `
      Reason: ${deleteReason}`
      }
      await insertNote({
        note: noteText,
        category: 'ledger_update',
        created_by_admin_id: admin.id,
        resource_id: rentalId,
        resource_type: 'rentals',
        priority: Note_Priorities_Enum.Default
      })
      onClose()
      Promise.resolve(onSuccess?.()).catch((error) => {
        console.error('Unable to refresh ledger after deleting ledger item', error)
      })
    } catch (error) {
      setErrorMessage(getDeleteErrorMessage(error, type))
    } finally {
      setIsDeleting(false)
    }
  }, [
    admin.id,
    amount,
    date,
    deleteReason,
    id,
    insertNote,
    liabilityLabel,
    onClose,
    onSuccess,
    rentalId,
    selectedReversalDate,
    type,
    usesBuildiumReversal
  ])

  if (type === 'Payment') {
    return null
  }

  return (
    <>
      <IconButton
        h='28px'
        minW='28px'
        variant='ghost'
        disabled={isDeleting}
        aria-label={`Delete ${type}`}
        icon={isDeleting ? <Spinner /> : <TrashIcon />}
        onClick={() => {
          setErrorMessage(null)
          setDeleteReason(undefined)
          resetReversalDateChoice()
          onOpen()
        }}
      />
      <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Delete {type}
            </AlertDialogHeader>
            <AlertDialogBody>
              {blockedDeleteMessage ? (
                <Alert status='error'>
                  <AlertIcon />
                  {blockedDeleteMessage}
                </Alert>
              ) : (
                <>
                  Are you sure you want to delete this {type}? This action cannot be undone.
                  <br />
                  <FormControl isRequired isDisabled={isDeleting}>
                    <FormLabel>Reason for deletion</FormLabel>
                    <Textarea
                      value={deleteReason}
                      onChange={(evt) => setDeleteReason(evt.target.value)}
                    />
                    <FormHelperText>Explain why this charge should be removed.</FormHelperText>
                  </FormControl>
                  {usesBuildiumReversal && (
                    <FormControl
                      mt={4}
                      isDisabled={isDeleting || isBooksLockLoading || isBooksLockError}
                    >
                      {isBooksLockLoading ? (
                        <Text>Checking Buildium books-lock status...</Text>
                      ) : isBooksLockError || !hasBuildiumReversalOption ? (
                        <BooksLockedNotice
                          lockDate={booksLock?.lockDate}
                          isError={isBooksLockError}
                        />
                      ) : (
                        <BuildiumReversalDateNotice
                          canReverseOriginal={canReverseOriginal}
                          canReverseToday={canReverseToday}
                          ledgerItemLabel={ledgerItemLabel}
                          lockDate={booksLock?.lockDate}
                          originalReversalDate={originalReversalDate}
                          selectedReversalDate={selectedReversalDate}
                          today={today}
                          onChange={setSelectedReversalDate}
                        />
                      )}
                    </FormControl>
                  )}
                  {errorMessage && (
                    <Alert status='error' mt={4}>
                      <AlertIcon />
                      {errorMessage}
                    </Alert>
                  )}
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button isDisabled={isDeleting} ref={cancelRef} onClick={onClose}>
                {blockedDeleteMessage ? 'Close' : 'Cancel'}
              </Button>
              {!blockedDeleteMessage && (
                <Button
                  isDisabled={
                    isDeleting ||
                    !deleteReason?.trim() ||
                    (usesBuildiumReversal &&
                      (isBooksLockLoading ||
                        isBooksLockError ||
                        !hasBuildiumReversalOption ||
                        !selectedReversalDate))
                  }
                  colorScheme='red'
                  ml={3}
                  onClick={handleDeletion}
                >
                  Delete
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
